import {type TranslationObject} from '@colonist/i18n'
import {ViewPopup} from '../ui_popup/ViewPopup.ts'
import {type ViewPopupOptions} from '../ui_popup/ViewPopupOptions.ts'
import {createDivIn, createHeaderIn, createTableIn, createSelectIn, createImgIn, createTHIn, createTHEADIn, createTBIn, createSpanIn} from '../ui_utils/DomUtils.ts'
import {UIPopupProfileController} from './UIPopupProfileController.ts'
import {LobbyImage} from '@colonist/types'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints.ts'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper.ts'
import {type AjaxDataTypes} from '../../../../shared/AjaxDataTypes.ts'
import {UIPopupManager} from '../ui_popup/UIPopupManager.ts'
import {clientI18n} from '../ClientI18n.ts'

export function getProfilePopupElementId(id: string, isPopup: boolean) {
    return `${isPopup ? 'profile_popup_' : ''}` + id
}

export enum ProfilePopupSelectedTab {
    Overview = 0,
    Ranked = 1,
    History = 2,
    Items = 3,
}

export class UIViewProfilePopup extends ViewPopup {
    private readonly username: string
    private static previousUsernames: string[] = []
    private clearHistoryOnClose = true

    private constructor(username: string, stickyPlayerInfo: AjaxDataTypes.PublicProfileDataStickyPlayerInfo, selectedTab: ProfilePopupSelectedTab) {
        const title: TranslationObject = {key: 'strings:colonistCommon.utils.#buffer', options: {value: `${username}'s Profile`}}

        const backButtonAction: () => void = () => {
            const previousUsername = UIViewProfilePopup.previousUsernames.pop()
            if(previousUsername == undefined) return
            void UIViewProfilePopup.create(previousUsername)
        }

        const checkAction: () => void = () => undefined
        const xAction: () => void = () => undefined
        const canCancel: boolean = true
        const options: ViewPopupOptions = {
            showXButton: false,
            showCheckButton: false,
            showXonHeader: true,
            backButton: {
                showBackButton: UIViewProfilePopup.previousUsernames.length > 0,
                backButtonAction: backButtonAction,
            },
        }

        super(title, undefined, checkAction, xAction, canCancel, options)
        this.username = username

        this.removeAction = () => {
            if(!this.clearHistoryOnClose) return
            UIViewProfilePopup.previousUsernames.length = 0
        }

        this.makeLarge()
        this.removeFooter()

        this.createProfileHTML()

        new UIPopupProfileController(username, stickyPlayerInfo, selectedTab)
    }

    static init(username: string, selectedTab?: ProfilePopupSelectedTab) {
        let previousProfilePopupUsername: string | undefined
        UIPopupManager.activePopups.forEach((existingPopup) => {
            if(existingPopup instanceof UIViewProfilePopup) {
                previousProfilePopupUsername = existingPopup.username
                return
            }
        })

        if(previousProfilePopupUsername == username) {
            return
        }

        if(previousProfilePopupUsername != undefined) {
            UIViewProfilePopup.previousUsernames = UIViewProfilePopup.previousUsernames.filter((existingUsername) => existingUsername != username)
            UIViewProfilePopup.previousUsernames.push(previousProfilePopupUsername)
        }

        void UIViewProfilePopup.create(username, selectedTab)
    }

    private static async create(username: string, selectedTab: ProfilePopupSelectedTab = ProfilePopupSelectedTab.Overview): Promise<void> {
        UIPopupManager.activePopups.forEach((existingPopup) => {
            if(existingPopup instanceof UIViewProfilePopup) {
                existingPopup.clearHistoryOnClose = false
                return
            }
        })

        UIPopupManager.closeAllPopups()

        const stickyPlayerInfo = await UIViewProfilePopup.getStickyPlayerInfo(username)
        if(stickyPlayerInfo == undefined) return

        const popup = new UIViewProfilePopup(username, stickyPlayerInfo, selectedTab)
        popup.show()
    }

    private static async getStickyPlayerInfo(username: string) {
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiUserProfile.stickyPlayerInfo(username), undefined)
        if(response == undefined) {
            return
        }
        return response.json as AjaxDataTypes.PublicProfileDataStickyPlayerInfo
    }

    private createProfileHTML() {
        const mainContainer = createDivIn(this.body, 'main_container_block main_container_singleton_page profile-page-main-container profile-popup-container')

        createDivIn(mainContainer, 'singleton_page_sidebar', 'profile_popup_profile_sidebar')

        const contentContainer = createDivIn(mainContainer, 'singleton_page_main_section')

        UIViewProfilePopup.createStickyPlayerInfo(contentContainer, this.username)
        UIViewProfilePopup.createOverviewSection(contentContainer)
        UIViewProfilePopup.createRankedSection(contentContainer)
        UIViewProfilePopup.createHistorySection(contentContainer)
        UIViewProfilePopup.createItemsSection(contentContainer)
    }

    private static createStickyPlayerInfo(container: HTMLElement, username: string) {
        const stickyPlayerInfo = createDivIn(container, 'sticky_player_info')

        const textContainer = createDivIn(stickyPlayerInfo, 'sticky_player_info_text_container', 'profile_popup_sticky_player_info_text_container')

        createDivIn(textContainer, 'profile-icon-wrapper', 'profile_popup_sticky_player_info_icon')

        createHeaderIn(textContainer,
            {key: 'strings:colonistCommon.utils.#buffer', options: {value: username}},
            'sticky_player_info_username', 'profile_popup_sticky_player_info_username', 'H1')

        const dropdown = createDivIn(textContainer, 'component-dropdown-container', 'profile_popup_sticky_player_info_dropdown')

        const dropdownButton = createDivIn(dropdown, 'component-dropdown-arrow', 'profile_popup_sticky_player_info_dropdown_button')

        createImgIn(dropdownButton, LobbyImage.DownArrow, 'component-icon-down-arrow')

        createDivIn(dropdown, 'component-dropdown-content')
    }

    private static createOverviewSection(container: HTMLElement) {
        const overviewSection = createDivIn(container, 'hidden static_center_scroll_profile', 'profile_popup_overview-container')

        createHeaderIn(overviewSection, {key: 'strings:profilePage.overview.title'}, '', '', 'H2')

        const userInfoContainer = createDivIn(overviewSection, 'user_info_container')

        const totalGamesItem = createDivIn(userInfoContainer, 'user_info_item user_info_item_top_row')
        totalGamesItem.setAttribute('data-tooltip', clientI18n.t('strings:profilePage.overview.table.totalGames.tooltip'))
        createHeaderIn(totalGamesItem, {key: 'strings:profilePage.overview.table.totalGames.title'}, '', '', 'H3')
        createHeaderIn(totalGamesItem, {key: 'strings:colonistCommon.utils.#buffer', options: {value: '?'}}, '', 'profile_popup_total_games_stat', 'H3')

        const winPercentageItem = createDivIn(userInfoContainer, 'user_info_item user_info_item_top_row')
        winPercentageItem.setAttribute('data-tooltip', clientI18n.t('strings:profilePage.overview.table.winPercentage.tooltip'))
        createHeaderIn(winPercentageItem, {key: 'strings:profilePage.overview.table.winPercentage.title'}, '', '', 'H3')
        createHeaderIn(winPercentageItem, {key: 'strings:colonistCommon.utils.#buffer', options: {value: '?'}}, '', 'profile_popup_win_ratio_stat', 'H3')

        const karmaItem = createDivIn(userInfoContainer, 'user_info_item user_info_item_bottom_row')
        karmaItem.setAttribute('data-tooltip', clientI18n.t('strings:profilePage.overview.table.karma.tooltip'))
        createHeaderIn(karmaItem, {key: 'strings:profilePage.overview.table.karma.title'}, '', '', 'H3')
        createHeaderIn(karmaItem, {key: 'strings:colonistCommon.utils.#buffer', options: {value: '?/?'}}, '', 'profile_popup_karma_stat', 'H3')

        const pointsPerGameItem = createDivIn(userInfoContainer, 'user_info_item user_info_item_bottom_row')
        pointsPerGameItem.setAttribute('data-tooltip', clientI18n.t('strings:profilePage.overview.table.pointsPerGame.tooltip'))
        createHeaderIn(pointsPerGameItem, {key: 'strings:profilePage.overview.table.pointsPerGame.title'}, '', '', 'H3')
        createHeaderIn(pointsPerGameItem, {key: 'strings:colonistCommon.utils.#buffer', options: {value: '?'}}, '', 'profile_popup_pg_stat', 'H3')

        const totalPointsItem = createDivIn(userInfoContainer, 'user_info_item user_info_item_bottom_row')
        totalPointsItem.setAttribute('data-tooltip', clientI18n.t('strings:profilePage.overview.table.points.tooltip'))
        createHeaderIn(totalPointsItem, {key: 'strings:profilePage.overview.table.points.title'}, '', '', 'H3')
        createHeaderIn(totalPointsItem, {key: 'strings:colonistCommon.utils.#buffer', options: {value: '?'}}, '', 'profile_popup_points_stat', 'H3')

        createDivIn(overviewSection, 'user-badges-container', 'profile_popup_user-badges-container')
    }

    private static createRankedSection(container: HTMLElement) {
        const rankedSection = createDivIn(container, 'hidden static_center_scroll_profile', 'profile_popup_ranked-container')

        const rankedHeader = createDivIn(rankedSection, 'profile_ranked_section_header')
        createHeaderIn(rankedHeader, {key: 'strings:profilePage.navigationTabs.ranked'}, '', '', 'H2')
        createSelectIn(rankedHeader, '', 'profile_popup_profile_ranked_season_dropdown', () => undefined)

        const rankedCardsContainer = createDivIn(rankedSection, 'ranked_mode_player_info_cards_container')

        UIViewProfilePopup.createRankedCard(rankedCardsContainer, 'profile_popup_ranked_mode_player_info_card_base',
            LobbyImage.GameModeBase, 'settlers of catan base game icon',
            {key: 'strings:leaderboardPage.gameModes.baseGame'}, 'base')

        UIViewProfilePopup.createRankedCard(rankedCardsContainer, 'profile_popup_ranked_mode_player_info_card_1v1',
            LobbyImage.GameMode1v1, 'colonist 1v1 game mode icon',
            {key: 'strings:leaderboardPage.gameModes.1v1'}, '1v1')

        this.createRankedCard(rankedCardsContainer, 'profile_popup_ranked_mode_player_info_card_citiesandknights',
            LobbyImage.GameModeCnK, 'settlers of catan cities and knights icon',
            {key: 'strings:leaderboardPage.gameModes.citiesAndKnights'}, 'citiesandknights')
    }

    private static createRankedCard(container: HTMLElement, cardId: string, iconSrc: LobbyImage, iconAlt: string, titleText: TranslationObject, mode: string) {
        const card = createDivIn(container, 'ranked_mode_player_info_card')
        card.id = cardId

        const cardHeader = createDivIn(card, 'ranked-card-header')
        const headerTitle = createDivIn(cardHeader, 'ranked-card-header-title')

        createImgIn(headerTitle, iconSrc, iconAlt)

        createSpanIn(headerTitle, '', titleText)

        const cardContent = createDivIn(card, 'ranked-card-content')
        createDivIn(cardContent, 'ranked_mode_player_progress_container', `profile_popup_ranked_mode_player_progress_${mode}_container`)

        createDivIn(cardContent, 'ranked_mode_player_stats_container', `profile_popup_ranked_mode_player_stats_${mode}_container`)
    }

    private static createHistorySection(container: HTMLElement) {
        const historySection = createDivIn(container, 'hidden static_center_scroll_profile', 'profile_popup_game-history-container')

        createHeaderIn(historySection, {key: 'strings:profilePage.history.title'}, '', '', 'H2')

        const historyContainer = createDivIn(historySection, 'profile_section game-history-table-container profile_container')
        const table = createTableIn(historyContainer)

        const thead = createTHEADIn(table, 'game-history-table-head', 'profile_popup_game-history-table-head')

        const rowHead = thead.insertRow()
        createTHIn(rowHead, undefined, {key: 'strings:profilePage.history.table.date.title'})
        createTHIn(rowHead, undefined, {key: 'strings:profilePage.history.table.rank.title'})
        createTHIn(rowHead, 'center', {key: 'strings:profilePage.history.table.opponents.title'})
        createTHIn(rowHead, 'right', {key: 'strings:profilePage.history.table.duration.title'})
        createTHIn(rowHead, 'center', {key: 'strings:profilePage.history.table.replay.title'})

        createTBIn(table, 'game-history-table-body', 'profile_popup_game-history-table-body')
    }

    private static createItemsSection(container: HTMLElement) {
        const itemsSection = createDivIn(container, 'hidden static_center_scroll_profile', 'profile_popup_items-container')

        createHeaderIn(itemsSection, {key: 'strings:profilePage.navigationTabs.items'}, '', '', 'H2')

        const itemsContainer = createDivIn(itemsSection, 'profile_container')
        const itemsPartition = createDivIn(itemsContainer, 'user_info_partition')
        createDivIn(itemsPartition, 'user_info_items_list_container', 'profile_popup_profile_items_div')
    }
}
